import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { Truck, Loader2, PackageCheck, MapPin, PenLine } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "driver" ? "/driver" : "/admin");
    } catch (e) {
      setError(apiErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col lg:flex-row">
      {/* Brand panel */}
      <div className="depot-grid relative hidden flex-1 flex-col justify-between overflow-hidden bg-[#0B192C] p-12 text-white lg:flex">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#FF6500]">
            <Truck size={24} />
          </div>
          <span className="font-head text-xl font-extrabold tracking-tight">GS Pacific ePOD</span>
        </div>
        <div className="relative z-10">
          <h1 className="font-head text-4xl font-black leading-tight xl:text-5xl">
            Proof of Delivery,<br /><span className="text-[#FF6500]">captured in the field.</span>
          </h1>
          <p className="mt-5 max-w-md text-slate-300">
            GPS-verified arrivals, sign-on-glass receipts and instant ePOD documents for Australian
            wholesale & FMCG distribution.
          </p>
          <div className="mt-8 flex flex-wrap gap-5 text-sm text-slate-200">
            <span className="flex items-center gap-2"><MapPin size={18} className="text-[#FF6500]" /> GPS geofence</span>
            <span className="flex items-center gap-2"><PenLine size={18} className="text-[#FF6500]" /> Digital signature</span>
            <span className="flex items-center gap-2"><PackageCheck size={18} className="text-[#FF6500]" /> Instant POD PDF</span>
          </div>
        </div>
        <p className="relative z-10 text-xs text-slate-400">ABN 48 102 938 481 · Eastern Creek Distribution Centre, Sydney NSW</p>
      </div>

      {/* Form */}
      <div className="flex flex-1 items-center justify-center bg-[#F8FAFC] p-6">
        <div className="w-full max-w-sm animate-slide-up">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#FF6500] text-white">
              <Truck size={24} />
            </div>
            <span className="font-head text-xl font-extrabold text-[#0B192C]">GS Pacific ePOD</span>
          </div>
          <h2 className="font-head text-2xl font-bold text-[#0B192C]">Sign in</h2>
          <p className="mt-1 text-sm text-slate-500">Access your delivery command centre.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email</label>
              <input
                data-testid="login-email-input"
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none focus:border-[#0B192C] focus:ring-2 focus:ring-[#0B192C]/10"
                placeholder="you@gspacific.com.au"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Password</label>
              <input
                data-testid="login-password-input"
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none focus:border-[#0B192C] focus:ring-2 focus:ring-[#0B192C]/10"
                placeholder="••••••••"
              />
            </div>
            {error && <div data-testid="login-error" className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}
            <button
              data-testid="login-submit-button"
              type="submit" disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#0B192C] py-3 font-bold text-white tap-shrink hover:bg-[#1E3E62] disabled:opacity-60"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Sign in"}
            </button>
            <div className="text-center">
              <Link to="/forgot-password" className="text-sm font-medium text-[#1E3E62] hover:underline" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
